import React, { useState, useEffect, useCallback } from 'react';

/**
 * CollapsibleSection — a large, page-level collapsible region.
 *
 * The app had ~20 hand-rolled copies of this same toggle (ExplorerControls,
 * SimulationSettingsPanel, HeatmapSettingsPanel, WatchlistGroups, …) plus an
 * orphaned `ExpandableSection` nothing imported. This is the consolidated
 * version, styled for a whole page section rather than an inline "show more".
 *
 * Two behaviors that matter and which most of those copies lacked:
 *
 *   - **Unmounts on collapse.** Children stop rendering entirely, so a hidden
 *     results table isn't recomputing memos or holding chart instances alive.
 *     (The screener's AccordionFormSection instead keeps children mounted under
 *     a CSS max-height, which is right for a form wizard preserving input state
 *     but wrong for heavy read-only content.)
 *   - **Optional localStorage persistence** via `storageKey`, so a section the
 *     user collapsed stays collapsed on their next visit.
 *
 * Props:
 *   title           string   — section heading
 *   subtitle        node     — small muted line under the title
 *   summary         node     — shown in the header only while COLLAPSED, so a
 *                              closed section can still report its state
 *   defaultExpanded bool     — initial state when nothing is persisted
 *   storageKey      string   — localStorage key; omit for ephemeral state
 *   headerActions   node     — buttons in the header; clicks do NOT toggle
 *   dataTour        string   — value for the data-tour anchor attribute
 *   expandSignal    number   — bump this counter to force the section open from
 *                              outside, e.g. when results arrive that the user
 *                              needs to see. Mirrors the `collapseSignal` latch
 *                              in ScreenerSavedParams. The user can still
 *                              collapse it again afterwards.
 */
export default function CollapsibleSection({
  title,
  subtitle,
  summary,
  defaultExpanded = true,
  storageKey,
  headerActions,
  dataTour,
  expandSignal = 0,
  className = '',
  children,
}) {
  const [isExpanded, setIsExpanded] = useState(() => {
    if (!storageKey) return defaultExpanded;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved === 'true') return true;
      if (saved === 'false') return false;
    } catch {
      /* localStorage unavailable (private mode, blocked cookies) */
    }
    return defaultExpanded;
  });

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, String(isExpanded));
    } catch {
      /* noop */
    }
  }, [storageKey, isExpanded]);

  // Open on demand from the parent. Guarded on > 0 so the initial render — and
  // therefore the persisted/default state — is never overridden.
  useEffect(() => {
    if (expandSignal > 0) setIsExpanded(true);
  }, [expandSignal]);

  const toggle = useCallback(() => setIsExpanded((prev) => !prev), []);

  const handleKeyDown = (e) => {
    // Space and Enter activate a role="button", per native button behavior.
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className={`pf-section ${className}`} data-tour={dataTour}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        className={`pf-section__header ${isExpanded ? 'pf-section__header--open' : ''}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="pf-section__chevron"
            style={{ transform: isExpanded ? 'rotate(90deg)' : 'none' }}
            aria-hidden="true"
          >
            ›
          </span>
          <div className="min-w-0">
            <h3 className="pf-section__title">{title}</h3>
            {subtitle && <p className="pf-section__subtitle">{subtitle}</p>}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {!isExpanded && summary && (
            <span className="pf-section__summary hidden sm:block">{summary}</span>
          )}
          {headerActions && (
            // Header buttons live inside the toggle row, so their clicks must
            // not bubble up and collapse the section out from under them.
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {headerActions}
            </div>
          )}
        </div>
      </div>

      {isExpanded && <div className="pf-section__body">{children}</div>}
    </div>
  );
}
